import React, { useCallback, useEffect, useState } from 'react';
import CardShell from '@/components/CardShell';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import CtaButton from '@/components/CtaButton';
import VerifyScreen from '@/screens/VerifyScreen';
import DeadLinkScreen from '@/screens/DeadLinkScreen';
import SetUpScreen from '@/screens/SetUpScreen';
import SuccessScreen from '@/screens/SuccessScreen';
import ManageScreen from '@/screens/ManageScreen';
import TurnOffConfirmScreen from '@/screens/TurnOffConfirmScreen';
import InvoiceRedirectScreen from '@/screens/InvoiceRedirectScreen';
import useHostedEntry from './useHostedEntry';
import { getAutoPayStatus, toggleAutoPay } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import { amountPerPeriod, assignmentChargeCents } from '@/format';
import session from '@/session';

/** Read + strip Stripe's 3DS-return query params, keeping ?t= intact. */
const consumeResumeSecret = () => {
	const params = new URLSearchParams(window.location.search);
	const secret = params.get('setup_intent_client_secret');
	if (!secret) return null;
	['setup_intent', 'setup_intent_client_secret', 'redirect_status'].forEach((key) => params.delete(key));
	const search = params.toString();
	window.history.replaceState(null, '', window.location.pathname + (search ? `?${search}` : ''));
	return secret;
};

/**
 * The /auto-pay state machine. Steps are component state, NOT routes — the
 * link token must stay in the URL so an expired session can drop back to
 * verify, and deep-linking into (say) the success step is meaningless.
 */
const AutoPayFlow = () => {
	const entry = useHostedEntry();
	const [resumeSecret, setResumeSecret] = useState(consumeResumeSecret);

	const [step, setStep] = useState('loading'); // loading | setup | manage | success | turnoff | invoice | dead | error
	const [status, setStatus] = useState(null);
	const [selectedId, setSelectedId] = useState(null);
	const [loadError, setLoadError] = useState(null);
	const [turnOffBusy, setTurnOffBusy] = useState(false);
	const [turnOffError, setTurnOffError] = useState(null);

	const loadStatus = useCallback(async (goal = 'auto') => {
		setStep('loading');
		setLoadError(null);
		try {
			const active = session.get();
			const fresh = await getAutoPayStatus(active.student_id);
			setStatus(fresh);

			if (!fresh.assignments.length) {
				setStep('dead');
				return null;
			}

			setSelectedId((current) => {
				const preferred =
					fresh.assignments.find((a) => a.assignment_id === (current ?? active.assignment_id)) ?? fresh.assignments[0];
				return preferred.assignment_id;
			});

			if (goal === 'success') setStep('success');
			else if (goal === 'manage') setStep('manage');
			else {
				const preferred =
					fresh.assignments.find((a) => a.assignment_id === (selectedId ?? active.assignment_id)) ??
					fresh.assignments[0];
				setStep(preferred.auto_pay_enabled ? 'manage' : 'setup');
			}
			return fresh;
		} catch (err) {
			const parsed = parseHostedError(err);
			if (parsed.type !== 'session') {
				setLoadError(parsed.message);
				setStep('error');
			}
			return null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (entry.phase === 'ready') loadStatus(resumeSecret ? 'setup-resume' : 'auto');
	}, [entry.phase, loadStatus, resumeSecret]);

	const assignment = status?.assignments.find((a) => a.assignment_id === selectedId) ?? status?.assignments[0];
	// Context branding when we came through verify; status covers sessions
	// that never called the pre-auth context endpoint (webview hand-off,
	// refreshes with a live session).
	const school = entry.context?.school ?? status?.school;
	const schoolName = school?.name;
	// Full name from the authenticated status response; the pre-auth context
	// only carries the first name (the last name is the verify secret).
	const studentName = status?.student_name ?? entry.context?.student_first_name;

	const handleTurnOffConfirm = async () => {
		setTurnOffBusy(true);
		setTurnOffError(null);
		try {
			await toggleAutoPay(assignment.assignment_id, { enabled: false });
			await loadStatus('auto');
		} catch (err) {
			setTurnOffError(parseHostedError(err).message);
		} finally {
			setTurnOffBusy(false);
		}
	};

	const switcher =
		status && status.assignments.length > 1 ? (
			<div className="flex w-full flex-wrap gap-[8px]">
				{status.assignments.map((a) => (
					<button
						key={a.assignment_id}
						type="button"
						onClick={() => {
							setSelectedId(a.assignment_id);
							setStep(a.auto_pay_enabled ? 'manage' : 'setup');
						}}
						className={`rounded-full border px-[14px] py-[6px] text-[12px] font-semibold ${
							a.assignment_id === selectedId
								? 'border-[#676AF6] bg-[#f6fbff] text-[#676AF6]'
								: 'border-[#d5d7da] bg-white text-[#7d7d7d]'
						}`}
					>
						{a.plan_name ?? 'Tuition'} · {amountPerPeriod(assignmentChargeCents(a), a.currency, a.frequency)}
					</button>
				))}
			</div>
		) : null;

	const body = () => {
		if (entry.phase === 'dead') return <DeadLinkScreen variant={entry.deadVariant} />;
		if (entry.phase === 'verify') {
			return (
				<VerifyScreen
					context={entry.context}
					onSubmit={entry.verify}
					error={entry.verifyError}
					busy={entry.verifyBusy}
					notice={entry.notice}
				/>
			);
		}
		if (entry.phase === 'loading' || step === 'loading') return <Spinner label="Loading…" />;

		if (step === 'dead') return <DeadLinkScreen variant="no_plans" />;
		if (step === 'error') {
			return (
				<div className="flex flex-col gap-[18px]">
					<ErrorBanner>{loadError}</ErrorBanner>
					<CtaButton onClick={() => loadStatus('auto')}>Try again</CtaButton>
				</div>
			);
		}
		if (step === 'invoice') {
			return (
				<InvoiceRedirectScreen
					studentId={status.student_id}
					studentName={studentName}
					onBack={() => setStep('setup')}
				/>
			);
		}
		if (step === 'success') {
			return (
				<SuccessScreen
					status={status}
					assignment={assignment}
					studentName={studentName}
					onManage={() => setStep('manage')}
				/>
			);
		}
		if (step === 'turnoff') {
			return (
				<TurnOffConfirmScreen
					schoolName={schoolName}
					onKeep={() => setStep('manage')}
					onConfirm={handleTurnOffConfirm}
					busy={turnOffBusy}
					error={turnOffError}
				/>
			);
		}
		if (step === 'manage') {
			return (
				<ManageScreen
					school={school}
					status={status}
					assignment={assignment}
					onTurnOff={() => {
						setTurnOffError(null);
						setStep('turnoff');
					}}
					onUpdated={() => loadStatus('manage')}
					assignmentSwitcher={switcher}
				/>
			);
		}

		return (
			<SetUpScreen
				school={school}
				status={status}
				assignment={assignment}
				studentName={studentName}
				onEnabled={() => loadStatus('success')}
				onPayInvoiceInstead={() => setStep('invoice')}
				resumeClientSecret={resumeSecret}
				onResumeConsumed={() => setResumeSecret(null)}
				assignmentSwitcher={switcher}
			/>
		);
	};

	return <CardShell>{body()}</CardShell>;
};

export default AutoPayFlow;
